/**
 * Similar Listings Section Switch
 * 
 * Control: Current implementation (SimilarListingsSections with its own headings)
 * Variation: New design using ContentSectionTemplate
 */

import { Box } from '@mui/material';
import { AllowedExperimentKey } from '@tenants/types';
import { ExperimentalSection } from 'ui/experimental-section';
import { ContentSectionTemplate } from 'components/atomic/templates/content-section';
import SimilarListingsSections from 'components/pages/address/public/components/similar-listings-sections';
import type Listing from 'data/listing';
import type { ComparableListing } from 'components/pages/address/public/components/similar-listings-sections/components/similar-listings';
import type { ListingCardProps } from 'components/listing-card/listing-card-v2';

// ============================================================================
// Types
// ============================================================================

type SimilarListingData = Pick<Listing, 'id'> & ComparableListing;

export interface SimilarListingsSectionProps {
  /** Section ID for scroll targeting */
  id: string;
  /** Listing data */
  listing: SimilarListingData;
  /** Whether to show carousel */
  showCarousel?: boolean;
  /** Whether to hide comparable listings */
  hideComparableListings?: boolean;
  /** Callback when listing card is clicked */
  onListingCardClick: ListingCardProps['onListingCardClick'];
}

// ============================================================================
// Control (Current Implementation)
// ============================================================================

function SimilarListingsSectionControl({
  id,
  listing,
  showCarousel,
  hideComparableListings,
  onListingCardClick,
}: SimilarListingsSectionProps) {
  // SimilarListingsSections has its own internal headings for each group
  return (
    <Box component="section" id={id} sx={{ scrollMarginTop: 'var(--scroll-offset, 150px)' }}>
      <SimilarListingsSections
        listing={listing}
        showCarousel={showCarousel}
        hideComparableListings={hideComparableListings}
        onListingCardClick={onListingCardClick}
      />
    </Box>
  );
}

// ============================================================================
// Variation (New Design with Templates)
// ============================================================================

function SimilarListingsSectionVariation({
  id,
  listing,
  showCarousel,
  hideComparableListings,
  onListingCardClick,
}: SimilarListingsSectionProps) {
  // SimilarListingsSections renders its own headings and card styling internally
  return (
    <ContentSectionTemplate id={id} hideHeading withCard={false}>
      <SimilarListingsSections
        listing={listing}
        showCarousel={showCarousel}
        hideComparableListings={hideComparableListings}
        onListingCardClick={onListingCardClick}
      />
    </ContentSectionTemplate>
  );
}

// ============================================================================
// Switch
// ============================================================================

export function SimilarListingsSectionSwitch(props: SimilarListingsSectionProps) {
  return (
    <ExperimentalSection
      experimentKey={AllowedExperimentKey.STICKY_HEADER_TABS}
      control={<SimilarListingsSectionControl {...props} />}
      variation={<SimilarListingsSectionVariation {...props} />}
    />
  );
}
